import {
    ClickEvent, DataSource, DataSourceExecutionEvent, SelectionEvent, SlideoutDecorator, TableRow, TableRowCreationEvent, TableRowMode
} from "@mcleod/components";
import { Alignment, Api, ModelRow } from "@mcleod/core";
import { CarrierOfferList } from "@mcleod/powerbroker";
import { AutogenLayoutMovementOfferTabs } from "./autogen/AutogenLayoutMovementOfferTabs";

export enum OfferTabType {
    WATERFALL,
    PENDING,
    HISTORY
}

export interface OfferTabEvent {
    type: OfferTabType;
    action: OfferTabEventAction;
    changedRow?: TableRow;
}

export enum OfferTabEventAction {
    SEARCH,
    SYNC_DATA,
    SELECT_OFFER,
    REMOTE_DATA_CHANGE,
    CONTENTS_CHANGED
}

export class MovementOfferTabs extends AutogenLayoutMovementOfferTabs {
    private movementID: string;
    private orderID: string;
    private isWaterfall: boolean;
    private brokerageMovementRow: ModelRow;

    async initialize(orderID: string, movementID: string, brokerageMovementRow: ModelRow) {
        this.movementID = movementID;
        this.orderID = orderID;
        this.brokerageMovementRow = brokerageMovementRow;
        this.addTabListeners();
        if (this.sourceMovement.isSearching) return;
        await this.setMovement();
        await Promise.all([
            this.initializeWaterfallOffersLayout(),
            this.initializePendingOffersLayout(),
            this.initializeOfferHistoryLayout()
        ]);
        this.tabsetOfferTables.visible = true;
    }

    async setMovement() {
        await this.sourceMovement.search({ "id": this.movementID });
    }

    addTabListeners() {
        this.layoutWaterfallOffers.onTabEvent = (offerTabEvent: OfferTabEvent) => this.waterfallTabEventHandler(offerTabEvent);
        this.layoutPendingOffers.onTabEvent = (offerTabEvent: OfferTabEvent) => this.pendingOffersTabEventHandler(offerTabEvent);
    }

    waterfallTabEventHandler(offerTabEvent: OfferTabEvent) {
        switch (offerTabEvent.action) {
            case OfferTabEventAction.SELECT_OFFER:
                this.buttonSkipCarrier.enabled = offerTabEvent.changedRow != null;
                break;
            case OfferTabEventAction.REMOTE_DATA_CHANGE: {
                this.layoutPendingOffers.search();
                break;
            }
        }
        this.updateTabTitle(OfferTabType.WATERFALL);
    }

    pendingOffersTabEventHandler(offerTabEvent: OfferTabEvent) {
        switch (offerTabEvent.action) {
            case OfferTabEventAction.SYNC_DATA:
                this.syncTabData(offerTabEvent.changedRow, OfferTabType.PENDING);
                break;
        }
        this.updateTabTitle(OfferTabType.PENDING);
    }

    async initializeWaterfallOffersLayout() {
        return this.layoutWaterfallOffers.initialize(this.getOrderSummaryInfo(), this.sourceMovement).then(() => {
            this.isWaterfall = this.sourceMovement.activeRow?.getBoolean("waterfall_in_progress", false);
            this.updateWaterfallComponents(this.isWaterfall);
        });
    }

    updateWaterfallComponents(waterfalling: boolean) {
        this.isWaterfall = waterfalling;
        this.tabWaterfallProgress.visible = waterfalling;
        this.tabsetOfferTables.selectedIndex = waterfalling ? 0 : 1;
        this.buttonCancelWaterfall.visible = waterfalling;
        this.buttonSkipCarrier.visible = waterfalling;
    }

    async initializePendingOffersLayout() {
        this.layoutPendingOffers.tableOffers.addRowCreateListener((event: TableRowCreationEvent) => {
            const tableRow = event.getTableRow();
            const modelRow = tableRow.data as ModelRow;
            if (tableRow.mode === TableRowMode.QUICK_ADD && modelRow._appending) {
                modelRow.addAfterPostListener((postEvent) => this.layoutOfferHistory.sourceCarrierOfferList.executeValidatedSearch());
            }
        });
        this.layoutPendingOffers.tableOffers.doOnRowDelete = (row => {
            this.layoutOfferHistory.sourceCarrierOfferList.executeValidatedSearch();
        })
        return this.layoutPendingOffers.initialize(this.orderID, this.sourceMovement, this.movementID);
    }

    async initializeOfferHistoryLayout() {
        this.layoutOfferHistory.searchpanelSearchOffers.visible = false;
        this.layoutOfferHistory.tableOffers.maxHeight = 400;
        this.layoutOfferHistory.textboxOrderId.enabled = false;
        this.layoutOfferHistory.sourceCarrierOfferList.searchRow.set("order_id", this.orderID)
        this.layoutOfferHistory.textboxOrderId.displayComponentData(this.layoutOfferHistory.sourceCarrierOfferList.searchRow, null, 0);
        this.layoutOfferHistory.sourceCarrierOfferList.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            this.updateTabTitle(OfferTabType.HISTORY);
        });
        this.layoutOfferHistory.sourceCarrierOfferList.executeValidatedSearch();
    }

    async syncTabData(changedRow: TableRow, type: OfferTabType) {
        this.findAndAddOrReplaceRow(type, changedRow);
        this.updateTabTitle(type);
    }

    findAndAddOrReplaceRow(type: OfferTabType, row: TableRow) {
        const table = this.getTableForType(type);
        const index = table.rows.findIndex(tableRow => tableRow.data.get("id") === row.data.get("id"));
        if (index >= 0) {
            table.rows[index].data.setValues(row.data);
        } else {
            table.addRow(row.data, { mode: TableRowMode.NONE }, { display: true, addToData: false });
        }
    }

    getTableForType(type: OfferTabType) {
        switch (type) {
            case OfferTabType.WATERFALL:
                return this.layoutWaterfallOffers.tableOffers;
            case OfferTabType.PENDING:
                return this.layoutPendingOffers.tableOffers;
        }
    }

    updateTabTitle(tabType: OfferTabType) {
        let rowCount: number;
        switch (tabType) {
            case OfferTabType.WATERFALL:
                rowCount = this.layoutWaterfallOffers.tableOffers.rowCount;
                this.tabWaterfallProgress.caption = `Waterfall Progress ${rowCount > 0 ? rowCount : ""}`;
                break;
            case OfferTabType.PENDING:
                rowCount = this.layoutPendingOffers.tableOffers.rowCount;
                this.tabPendingOffers.caption = `Pending Offers ${rowCount > 0 ? rowCount : "0"}`;
                break;
            case OfferTabType.HISTORY:
                rowCount = this.layoutOfferHistory.tableOffers.rowCount;
                this.tabOfferHistory.caption = `Offer History ${rowCount > 0 ? rowCount : "0"}`;
                break;
        }
    }

    getOrderSummaryInfo() {
        return {
            order_id: this.orderID,
            commodity: this.brokerageMovementRow.get("commodity"),
            distance: this.brokerageMovementRow.get("distance"),
            weight: this.brokerageMovementRow.get("weight"),
            equipment_type: this.brokerageMovementRow.get("equipment_type")
        }
    }

    public set stopCountText(stopCountText: string) {
        this.layoutPendingOffers.stopCountText = stopCountText;
    }

    async buttonCancelWaterfallOnClick(event: ClickEvent) {
        await Api.post("lme/powerbroker/move-locking", { movement_id: this.movementID, action: "cancel_waterfall" });
        await this.layoutWaterfallOffers.search();
    }

    async buttonSkipCarrierOnClick(event: ClickEvent) {
        const row: ModelRow = this.layoutWaterfallOffers.tableOffers.selectedRow?.data;
        if (row == null) return;
        await Api.post("lme/powerbroker/skip-waterfall-offer", { offer_id: row.get("id") });
        this.layoutWaterfallOffers.search();
    }

    tabsetOfferTablesAfterTabSelection(event: SelectionEvent) {
        const isWaterfallTab = event.newSelection == this.tabWaterfallProgress;
        this.buttonCancelWaterfall.visible = this.buttonSkipCarrier.visible = isWaterfallTab;
        this.buttonOfferHistory.visible = event.newSelection == this.tabOfferHistory;
        if (event.userInitiatedChange) {
            switch (event.newSelection) {
                // Waterfall gets live updates so new search not needed
                case this.tabPendingOffers:
                    this.layoutPendingOffers.search();
                    break;
                case this.tabWaterfallProgress:
                    this.layoutWaterfallOffers.search();
                    break;
                case this.tabOfferHistory:
                    this.layoutOfferHistory.sourceCarrierOfferList.executeValidatedSearch();
                    break;
            }
        }
    }

    buttonOfferHistoryOnClick(event: ClickEvent) {
        SlideoutDecorator.decorateAndSlideIn("lme/powerbroker/CarrierOfferList", {
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            title: "Offer History",
            transitionOptions: { direction: Alignment.CENTER },
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            layoutLoadListeners: (event) => {
                const layout = event.target as CarrierOfferList;
                layout.textboxOrderId.text = this.orderID;
                layout.sourceCarrierOfferList.executeValidatedSearch();
            }
        })
    }

    showAndConfigureTrackingTab(dataSource: DataSource) {
        this.tabTracking.visible = true;
        const hasBeenReplaced = this.layoutTracking.mainDataSource.childDataSources === dataSource.childDataSources;
        if (!hasBeenReplaced) this.layoutTracking.replaceMainDatasource(dataSource);
    }
}
